import { Row } from "@tanstack/react-table";

import { GeoJSONSource } from "maplibre-gl";
import { FeatureCollection, GeoJsonProperties } from "geojson";

import NotificationContent from "@/components/notification/notificationContent";
import {
  ExtendedColumnDef,
  SpatialAnalysisRow
} from "@/components/spatialAnalysis/spatialAnalysisTable/columns";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetSchemaLabel
} from "@/types/dataset";

import useToast, { EToastType } from "../ui/useToast";

type TypeGeomPreview = "grid" | "wkt";

export type PreviewData = {
  value: string | null;
  type: "grid" | "wkt";
};

interface IUsePreviewGeom {
  handleClose: () => void;
  selectedDatasetId: string | null | undefined;
  attributes?: DatasetAttributeSchema[];
}

export const usePreviewGeom = ({
  handleClose,
  selectedDatasetId,
  attributes
}: IUsePreviewGeom) => {
  const { handleHighlightDatasetFeatureByWkt } = useDataset();
  const { showToast } = useToast();
  const { map } = useMap();

  const handlePreviewClick = async (
    value: string | null,
    type: "grid" | "wkt"
  ) => {
    if (!value || !selectedDatasetId) {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
            description={MESSAGE.MAP_VIEW_ACTION_COLUMN_FAILED}
          />
        ),
        autoClose: 2000
      });
      return;
    }

    let highlightValue: string | GeoJsonProperties = value;

    if (type === "grid") {
      const source = map?.getSource<GeoJSONSource>(selectedDatasetId);
      if (!source) return;
      const h3OrGridName =
        attributes?.find(
          (attr) =>
            attr.data_label === DatasetSchemaLabel.GEOHASH ||
            attr.data_label === DatasetSchemaLabel.H3
        )?.attribute_name || "";

      const featureCollection = (await source.getData()) as FeatureCollection;

      const feature = featureCollection.features.find(
        (f) =>
          f.properties?.[h3OrGridName] === value ||
          f.properties?.["Grid ID"] === value ||
          f.properties?.["H3 ID"] === value
      );

      if (!feature) return;
      highlightValue = feature;
    }

    handleHighlightDatasetFeatureByWkt({
      datasetId: selectedDatasetId,
      value: highlightValue,
      type
    });

    handleClose();
  };

  const getPreviewDataFromRow = (row: Row<SpatialAnalysisRow>) => {
    const { grid_id, h3_id } = row.original;

    const valueGrid = row
      .getAllCells()
      .find(
        (cell) =>
          (cell.column.columnDef as ExtendedColumnDef<SpatialAnalysisRow>)
            ?.data_label === DatasetSchemaLabel.GEOHASH ||
          (cell.column.columnDef as ExtendedColumnDef<SpatialAnalysisRow>)
            ?.data_label === DatasetSchemaLabel.H3
      )
      ?.getValue() as string | undefined;

    const geometryValue = row
      .getAllCells()
      .find(
        (cell) =>
          (cell.column.columnDef as ExtendedColumnDef<SpatialAnalysisRow>)
            ?.type === DataSchemaType.GEOMETRY
      )
      ?.getValue() as string | undefined;

    const gridValue = valueGrid || grid_id || h3_id;
    const isGridType = !!gridValue && !geometryValue;

    const value = isGridType ? String(gridValue) : (geometryValue ?? null);
    const type: TypeGeomPreview = isGridType ? "grid" : "wkt";

    return { value, type };
  };

  return { handlePreviewClick, getPreviewDataFromRow };
};
